import React from 'react';
import {useCodeLikeInput} from '../useCodeLikeInput';
import styles from './CodeInput.module.css';

const CODE_LENGTH = 4;
const SHAKE_DURATION_MS = 820;
const STAGGER_DELAY_MS = 45;
const GLOW_YELLOW = 'rgba(250, 204, 21, 0.55)'; // close to bg-yellow-300
const GLOW_RED = 'rgba(253, 146, 124, 0.55)'; // close to bg-[#FD927C]

interface CodeInputProps {
	value: string;
	onChange: (value: string) => void;
	disabled?: boolean;
	errorNonce?: number;
	/** Hides the system keyboard (use with an on-screen keypad on narrow viewports). */
	suppressNativeKeyboard?: boolean;
}

export const CodeInput = React.forwardRef<HTMLInputElement, CodeInputProps>(function CodeInput(
	{value, onChange, disabled, errorNonce, suppressNativeKeyboard},
	ref,
) {
	const {digits, isErrorAnimating, setInputRef, getBubbleRef, handleMouseDown, handleBlur, handleNativeChange} =
		useCodeLikeInput({
			value,
			onChange,
			disabled,
			errorNonce,
			codeLength: CODE_LENGTH,
			shakeDurationMs: SHAKE_DURATION_MS,
			staggerDelayMs: STAGGER_DELAY_MS,
			glowDefault: GLOW_YELLOW,
			glowError: GLOW_RED,
			forwardedRef: ref,
		});

	return (
		<div className={`${styles.root} ${isErrorAnimating ? 'shake' : ''}`}>
			<div className={styles.digitsRow} onMouseDown={handleMouseDown}>
				{Array.from({length: CODE_LENGTH}).map((_, idx) => {
					const char = digits[idx] ?? '';

					return (
						<div
							key={idx}
							className={`${styles.cell} ${char ? `${styles.cellBorderFilled} ${styles.cellWithDigit}` : styles.cellBorderEmpty}`}
							aria-hidden="true"
						>
							{char ? (
								<span
									ref={getBubbleRef(idx)}
									className={`${styles.bubble} ${isErrorAnimating ? styles.bubbleError : styles.bubbleFilled}`}
								>
									{char}
								</span>
							) : null}
						</div>
					);
				})}
			</div>
			<input
				type="text"
				name={'code'}
				value={digits}
				onChange={handleNativeChange}
				onBlur={handleBlur}
				className={styles.hiddenInput}
				disabled={disabled}
				readOnly={suppressNativeKeyboard}
				minLength={CODE_LENGTH}
				maxLength={CODE_LENGTH}
				inputMode={suppressNativeKeyboard ? 'none' : 'numeric'}
				pattern="[0-9]*"
				autoFocus
				autoComplete="one-time-code"
				ref={setInputRef}
			/>
		</div>
	);
});
